import { createPortal } from 'preact/compat';
import { Tooltip } from './Tooltip';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'preact/hooks';
import type { FieldType } from '../../shared/types';
import { AppIcon } from './AppIcon';

export const TYPE_LABELS: Record<FieldType, string> = {
  short_text: 'Short answer', paragraph: 'Paragraph', choice: 'Multiple choice', checkboxes: 'Checkboxes',
  dropdown: 'Dropdown', linear_scale: 'Linear scale', rating: 'Rating',
  choice_grid: 'Multiple choice grid', checkbox_grid: 'Checkbox grid', date: 'Date', time: 'Time',
  acknowledgment: 'Acknowledgment', section: 'Section header', title_text: 'Title and description',
};
const GROUPS: FieldType[][] = [
  ['short_text', 'paragraph'], ['choice', 'checkboxes', 'dropdown'],
  ['linear_scale', 'rating', 'choice_grid', 'checkbox_grid'], ['date', 'time'],
];
const TYPES = GROUPS.flat();

export function QuestionTypeMenu({ value, onChange }: { value: FieldType; onChange: (type: FieldType) => void }) {
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number; width: number; maxHeight: number } | null>(null);
  const initialFocus = useRef(value);
  const search = useRef({ text: '', time: 0 });
  const close = (restoreFocus = true) => { setPosition(null); if (restoreFocus) trigger.current?.focus({ preventScroll: true }); };
  const focus = (type: FieldType) => {
    const option = panel.current?.querySelector<HTMLButtonElement>(`[data-type="${type}"]`);
    option?.focus({ preventScroll: true });
    option?.scrollIntoView?.({ block: 'nearest' });
  };
  const show = () => {
    const box = trigger.current!.getBoundingClientRect();
    const width = Math.min(box.width, window.innerWidth - 16);
    initialFocus.current = TYPES.includes(value) ? value : 'checkboxes';
    search.current = { text: '', time: 0 };
    setPosition({ left: Math.max(8, Math.min(box.right - width, window.innerWidth - width - 8)), top: Math.max(8, box.top),
      width, maxHeight: window.innerHeight - 16 });
  };
  useLayoutEffect(() => {
    if (!position || !panel.current) return;
    const menu = panel.current;
    const selected = menu.querySelector<HTMLButtonElement>(`[data-type="${initialFocus.current}"]`)!;
    const box = trigger.current!.getBoundingClientRect();
    const menuBox = menu.getBoundingClientRect();
    const optionBox = selected.getBoundingClientRect();
    const center = box.top + box.height / 2;
    const selectedCenter = optionBox.top - menuBox.top + menu.scrollTop + optionBox.height / 2;
    const fullHeight = Math.max(menu.scrollHeight + 2, menuBox.height);
    // The current type always sits over the button, as in a native select. Any of the list that would go past the top or
    // bottom of the window is cut off there, and reached by scrolling the menu.
    const naturalTop = center - selectedCenter;
    const top = Math.max(8, naturalTop);
    const maxHeight = Math.min(fullHeight - (top - naturalTop), window.innerHeight - 8 - top);
    menu.style.maxHeight = `${maxHeight}px`;
    menu.scrollTop = top - naturalTop;
    setPosition({ ...position, top, maxHeight });
    selected.focus({ preventScroll: true });
  }, [!!position]);
  useEffect(() => {
    if (!position) return;
    const outside = (event: Event) => {
      if (event.target instanceof Node && !panel.current?.contains(event.target) && !trigger.current?.contains(event.target)) close(false);
    };
    const scroll = (event: Event) => { if (!(event.target instanceof Node) || !panel.current?.contains(event.target)) close(); };
    const resize = () => close();
    document.addEventListener('pointerdown', outside);
    document.addEventListener('focusin', outside);
    window.addEventListener('scroll', scroll, true);
    window.addEventListener('resize', resize);
    return () => {
      document.removeEventListener('pointerdown', outside); document.removeEventListener('focusin', outside);
      window.removeEventListener('scroll', scroll, true); window.removeEventListener('resize', resize);
    };
  }, [!!position]);

  return <div class="question-type">
    <Tooltip text="Change question type"><button type="button" class="question-type-trigger" ref={trigger} aria-label="Question type"
      aria-haspopup="listbox" aria-describedby={`${id}-value`} aria-expanded={!!position} aria-controls={`${id}-types`}
      onClick={() => position ? close() : show()}
      onKeyDown={event => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); if (position) focus(TYPES.includes(value) ? value : 'checkboxes'); else show(); }
      }}>
      <QuestionTypeIcon type={value} /><span id={`${id}-value`}>{TYPE_LABELS[value]}</span><AppIcon name="chevron-down" />
    </button></Tooltip>
    {position && createPortal(<div class="question-type-menu" ref={panel} id={`${id}-types`} role="listbox" aria-label="Question type" style={position}
      onKeyDown={event => {
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); return; }
        // Resume the form's normal tab order instead of tabbing through the portal.
        if (event.key === 'Tab') { close(); return; }
        const index = TYPES.indexOf((document.activeElement as HTMLElement)?.dataset.type as FieldType);
        if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          focus(TYPES[event.key === 'Home' ? 0 : event.key === 'End' ? TYPES.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + TYPES.length) % TYPES.length]);
        } else if (event.key.length === 1 && event.key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey) {
          event.preventDefault();
          const text = (Date.now() - search.current.time < 700 ? search.current.text : '') + event.key.toLowerCase();
          const query = [...text].every(char => char === text[0]) ? text[0] : text;
          search.current = { text, time: Date.now() };
          const next = [...TYPES.slice(index + 1), ...TYPES.slice(0, index + 1)].find(type => TYPE_LABELS[type].toLowerCase().startsWith(query));
          if (next) focus(next);
        }
      }}>
      {GROUPS.map((group, index) => <div class="question-type-group" role="group" key={index}>
        {group.map(type => <button type="button" role="option" class="question-type-option" key={type} data-type={type} tabIndex={-1} aria-selected={type === value}
          onClick={() => { onChange(type); close(); }}><QuestionTypeIcon type={type} /><span>{TYPE_LABELS[type]}</span></button>)}
      </div>)}
    </div>, trigger.current?.closest('dialog') ?? document.body)}
  </div>;
}

function QuestionTypeIcon({ type }: { type: FieldType }) {
  return <svg class="app-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    {type === 'short_text' && <path d="M4 9h16M4 15h10" />}
    {type === 'paragraph' && <path d="M4 6h16M4 10h16M4 14h16M4 18h10" />}
    {type === 'choice' && <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4" fill="currentColor" stroke="none" /></>}
    {type === 'checkboxes' && <><rect x="3" y="3" width="18" height="18" rx="2" /><path d="m7 12 3 3 7-7" /></>}
    {type === 'dropdown' && <><circle cx="12" cy="12" r="9" /><path d="m8 10 4 4 4-4" /></>}
    {type === 'linear_scale' && <><path d="M4 12h16" />{[4,12,20].map(cx => <circle cx={cx} cy="12" r="2" fill="currentColor" key={cx} />)}</>}
    {type === 'rating' && <path d="m12 3 2.8 5.8 6.4.9-4.6 4.5 1.1 6.3-5.7-3-5.7 3 1.1-6.3-4.6-4.5 6.4-.9Z" />}
    {(type === 'choice_grid' || type === 'checkbox_grid') && [5,12,19].flatMap(x => [5,12,19].map(y => type === 'choice_grid' ? <circle cx={x} cy={y} r="2" fill="currentColor" stroke="none" key={`${x}-${y}`} /> : <rect x={x - 2} y={y - 2} width="4" height="4" fill="currentColor" stroke="none" key={`${x}-${y}`} />))}
    {type === 'date' && <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 11h18" /><rect x="7" y="14" width="4" height="3" fill="currentColor" stroke="none" /></>}
    {type === 'time' && <><circle cx="12" cy="12" r="9" /><path d="M12 6v6l4 3" /></>}
    {type === 'acknowledgment' && <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m6 12 2 2 4-4M15 10h3M15 14h3" /></>}
    {type === 'section' && <><rect x="3" y="4" width="18" height="5" rx="1" /><path d="M3 14h18M3 19h18" /></>}
    {type === 'title_text' && <path d="M3 5h12M9 5v14M5 19h8M15 11h6M18 11v8M16 19h4" />}
  </svg>;
}
